/**
 * React Router (v7/v8) provider implementation.
 *
 * @module
 */
import { I18nError } from '@molecule/app-i18n';
import { generatePath, matchPath, parseSearchParams, stringifyQuery } from './utilities.js';
/**
 * Creates a React Router adapter that implements the molecule Router interface.
 *
 * @example
 * ```tsx
 * import { useNavigate, useLocation, useParams, useSearchParams } from 'react-router'
 * import { createReactRouter, setRouter } from '@molecule/app-routing-react-router'
 * import { useEffect, useMemo } from 'react'
 *
 * function RouterProvider({ children }) {
 *   const navigate = useNavigate()
 *   const location = useLocation()
 *   const params = useParams()
 *   const [searchParams] = useSearchParams()
 *
 *   const router = useMemo(() => createReactRouter({
 *     navigate,
 *     location,
 *     params,
 *   }), [navigate, location, params])
 *
 *   useEffect(() => {
 *     setRouter(router)
 *   }, [router])
 *
 *   return children
 * }
 * ```
 * @param config - Configuration with React Router's `navigate` function, `location`, `params`, and optional `routes`.
 * @returns A molecule `Router` with navigation, guards, query/hash management, and route matching.
 */
export const createReactRouter = (config = {}) => {
    const { navigate: navigateFn, location: locationObj, params = {}, routes = [] } = config;
    let registeredRoutes = [...routes];
    const listeners = new Set();
    const guards = [];
    const namedRoutes = new Map();
    // Index named routes
    const indexRoutes = (routeList, prefix = '') => {
        for (const route of routeList) {
            const fullPath = prefix + route.path;
            if (route.name) {
                namedRoutes.set(route.name, { ...route, path: fullPath });
            }
            if (route.children) {
                indexRoutes(route.children, fullPath);
            }
        }
    };
    indexRoutes(routes);
    // Build current location
    const getCurrentLocation = () => {
        if (locationObj) {
            return {
                pathname: locationObj.pathname,
                search: locationObj.search,
                hash: locationObj.hash,
                state: locationObj.state,
                key: locationObj.key,
            };
        }
        if (typeof window !== 'undefined') {
            return {
                pathname: window.location.pathname,
                search: window.location.search,
                hash: window.location.hash,
                key: Math.random().toString(36).slice(2),
            };
        }
        return {
            pathname: '/',
            search: '',
            hash: '',
            key: Math.random().toString(36).slice(2),
        };
    };
    const normalizeParams = (p) => {
        const result = {};
        for (const [key, value] of Object.entries(p)) {
            if (value !== undefined) {
                result[key] = value;
            }
        }
        return result;
    };
    const runGuards = async (to, from) => {
        for (const guard of guards) {
            const result = await guard(to, from);
            if (result !== undefined && result !== true) {
                return result;
            }
        }
        return true;
    };
    const notify = (action) => {
        const location = getCurrentLocation();
        listeners.forEach((listener) => listener(location, action));
    };
    const doNavigate = async (path, options = {}) => {
        const { replace = false, preserveQuery, preserveHash, state } = options;
        const currentLocation = getCurrentLocation();
        let fullPath = path;
        if (preserveQuery && currentLocation.search) {
            fullPath += currentLocation.search;
        }
        if (preserveHash && currentLocation.hash) {
            fullPath += currentLocation.hash;
        }
        const newLocation = {
            pathname: fullPath.split('?')[0].split('#')[0],
            search: fullPath.includes('?') ? `?${fullPath.split('?')[1].split('#')[0]}` : '',
            hash: fullPath.includes('#') ? `#${fullPath.split('#')[1]}` : '',
            state,
            key: Math.random().toString(36).slice(2),
        };
        const guardResult = await runGuards(newLocation, currentLocation);
        if (guardResult === false)
            return;
        if (typeof guardResult === 'string') {
            doNavigate(guardResult, { replace: true });
            return;
        }
        if (typeof guardResult === 'object' && 'path' in guardResult) {
            doNavigate(guardResult.path, { replace: guardResult.replace });
            return;
        }
        if (navigateFn) {
            navigateFn(fullPath, { replace, state });
        }
        else if (typeof window !== 'undefined') {
            // Fallback to window.location
            if (replace) {
                window.location.replace(fullPath);
            }
            else {
                window.location.href = fullPath;
            }
        }
        notify(replace ? 'replace' : 'push');
    };
    const router = {
        getLocation: getCurrentLocation,
        getParams() {
            return normalizeParams(params);
        },
        getQuery: () => {
            const location = getCurrentLocation();
            const searchParams = new URLSearchParams(location.search);
            return parseSearchParams(searchParams);
        },
        getQueryParam: (key) => {
            const location = getCurrentLocation();
            const searchParams = new URLSearchParams(location.search);
            return searchParams.get(key) ?? undefined;
        },
        getHash: () => getCurrentLocation().hash,
        navigate: doNavigate,
        navigateTo(name, routeParams, query, options) {
            const route = namedRoutes.get(name);
            if (!route) {
                throw new I18nError('routing.error.routeNotFound', { name }, `Route "${name}" not found`);
            }
            let path = generatePath(route.path, routeParams);
            if (query) {
                path += stringifyQuery(query);
            }
            doNavigate(path, options);
        },
        back() {
            if (navigateFn) {
                navigateFn(-1);
            }
            else if (typeof window !== 'undefined') {
                window.history.back();
            }
        },
        forward() {
            if (navigateFn) {
                navigateFn(1);
            }
            else if (typeof window !== 'undefined') {
                window.history.forward();
            }
        },
        go(delta) {
            if (navigateFn) {
                navigateFn(delta);
            }
            else if (typeof window !== 'undefined') {
                window.history.go(delta);
            }
        },
        setQuery(query, options) {
            const location = getCurrentLocation();
            const path = location.pathname + stringifyQuery(query) + location.hash;
            doNavigate(path, options);
        },
        setQueryParam(key, value, options) {
            const query = this.getQuery();
            if (value === undefined) {
                delete query[key];
            }
            else {
                query[key] = value;
            }
            this.setQuery(query, options);
        },
        setHash(hash, options) {
            const location = getCurrentLocation();
            const normalizedHash = hash.startsWith('#') ? hash : `#${hash}`;
            const path = location.pathname + location.search + normalizedHash;
            doNavigate(path, options);
        },
        isActive(path, exact) {
            const location = getCurrentLocation();
            const match = matchPath(path, location.pathname, exact);
            return match !== null;
        },
        matchPath(pattern, pathToMatch) {
            return matchPath(pattern, pathToMatch);
        },
        generatePath(name, routeParams, query) {
            const route = namedRoutes.get(name);
            if (!route) {
                throw new I18nError('routing.error.routeNotFound', { name }, `Route "${name}" not found`);
            }
            let path = generatePath(route.path, routeParams);
            if (query) {
                path += stringifyQuery(query);
            }
            return path;
        },
        subscribe(listener) {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
        addGuard(guard) {
            guards.push(guard);
            return () => {
                const index = guards.indexOf(guard);
                if (index !== -1) {
                    guards.splice(index, 1);
                }
            };
        },
        registerRoutes(newRoutes) {
            registeredRoutes = [...registeredRoutes, ...newRoutes];
            indexRoutes(newRoutes);
        },
        getRoutes: () => registeredRoutes,
        destroy() {
            listeners.clear();
            guards.length = 0;
        },
    };
    return router;
};
/**
 * Default React Router provider (basic, no hooks). For full functionality, use
 * `createReactRouter` with `useNavigate`/`useLocation`/`useParams`.
 */
export const provider = createReactRouter();
//# sourceMappingURL=provider.js.map